import type { OrderStatus, OrderType } from './ordersApi.js';

/** Plain-language status wording, matching the server's own labels. */
const STATUS_LABELS: Record<OrderStatus, string> = {
  submitted: 'Submitted',
  in_build: 'In build',
  in_review: 'In review',
  live: 'Live',
  extended: 'Extended',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

/** What each status actually means for the agency, in one sentence. */
const STATUS_EXPLANATIONS: Record<OrderStatus, string> = {
  submitted: 'We have your order and your credits are set aside for it.',
  in_build: 'We are building the site now.',
  in_review: 'The build is done and being checked over.',
  live: 'The site is live and your credits have been used.',
  extended: 'The site is live for another year.',
  expired: 'The paid year has ended.',
  cancelled: 'This order was cancelled and your credits were given back.',
};

/**
 * A renewal is a different thing from a build and needs different words —
 * "we are building the site now" is nonsense on an extension (ruling C-4).
 */
const EXTENSION_STATUS_EXPLANATIONS: Partial<Record<OrderStatus, string>> = {
  submitted: 'We have your renewal and your credits are set aside for it.',
  extended: 'Done. The site is paid up for longer.',
  cancelled: 'This renewal was cancelled and your credits were given back.',
};

const STATUS_TONES: Record<OrderStatus, string> = {
  submitted: 'bg-sunken text-fg-2',
  in_build: 'bg-blue-soft text-blue-ink',
  in_review: 'bg-warn-soft text-warn',
  live: 'bg-ok-soft text-ok',
  extended: 'bg-ok-soft text-ok',
  expired: 'bg-sunken text-fg-muted',
  cancelled: 'bg-sunken text-fg-muted',
};

export function statusLabel(status: OrderStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function statusExplanation(status: OrderStatus, orderType?: OrderType): string {
  if (orderType && isExtensionOrder(orderType)) {
    return EXTENSION_STATUS_EXPLANATIONS[status] ?? STATUS_EXPLANATIONS[status] ?? '';
  }
  return STATUS_EXPLANATIONS[status] ?? '';
}

/**
 * A renewal, rather than something being built.
 *
 * The single place the distinction is made on the client, so no page has to
 * remember which enum value means "no build stages, nothing to deliver".
 */
export function isExtensionOrder(type: OrderType): boolean {
  return type === 'year_extension';
}

export function statusToneClasses(status: OrderStatus): string {
  return STATUS_TONES[status] ?? 'bg-sunken text-fg-2';
}

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  site_build: 'New site build',
  year_extension: 'Keeping a site live',
  content_refresh: 'Content refresh',
};

export function orderTypeLabel(type: OrderType): string {
  return ORDER_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

/** What the reservation means, said plainly. */
export function creditsStateExplanation(
  state: 'reserved' | 'committed' | 'released',
  credits: number,
): string {
  switch (state) {
    case 'reserved':
      return `${credits} credits are set aside for this order. They are not spent yet.`;
    case 'committed':
      return `${credits} credits were used for this order.`;
    case 'released':
      return `${credits} credits were given back.`;
    default:
      return '';
  }
}
